import CodeBlock from "../../common/code_block";
import ErrorCodesLink from "../../common/ErrorCodesLink";
import HeaderWithTag from "../../common/header_with_tag";
import { IGetTransactionDetailsRequest } from "../../types/types";
import TryGetTransactionDetails from "./try";
import { Link } from 'react-router-dom';
import { HOST_API_URL } from "../../config";

const requestCode: IGetTransactionDetailsRequest = {
  ttno: "XRT00020675",
};

const responseCode = `{
  "statusCode": 200,
  "message": "Success",
  "errorCode": null,
  "data": {
    "ttno": "XRT00020675",
    "sender": { "name": "Abdirazak Ibrahim Abdirahman" },
    "receiver": { "name": "Jama Ali Warsame", "phone": "252659114455" },
    "bankDetails": null,
    "amount": 100.0,
    "status": "Ready",
    "origRef": "ORIG123456",
    "date_Paid": null,
    "dated": "2026-08-25T09:14:02"
  }
}`;

const failureResponseCode = `{
  "statusCode": 400,
  "message": "Transaction not found",
  "errorCode": "DPI-4002",
  "data": null
}`;

function GetTransactionDetails() {
  return (
    <div className="page">
      <div className="app-content main-content mt-0">
        <div className="side-app">
          <div className="main-container container-fluid">
            <div className="page-header">
              <div>
                <h1 className="page-title">Get Transaction Details</h1>
              </div>
              <div className="ms-auto pageheader-btn">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="javascript:void(0);">Docs</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Get Transaction Details
                  </li>
                </ol>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="row">
                <div className="col-md-7" style={{ position: "relative" }}>
                  <div className="card-body border-end p-0">
                    <div className="p-5">
                      <HeaderWithTag
                        headerText="Get transaction details"
                        headerTag="description"
                      />
                      <p className="text-12">
                        <span className="badge badge-sm bg-blue text-white me-2">
                          GET
                        </span>
                        {HOST_API_URL}/Remittances/GetTransactionDetail?ttno={"{ttno}"}
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        Returns the current status and key details of one of your transactions.
                        Call this to confirm current state after submission or before performing
                        actions like cancellation. <code>ttno</code> accepts either the
                        transaction number returned by Send Remittance or the{" "}
                        <code>origRefNumber</code> you supplied when sending. Only transactions
                        sent from your own agent account are visible; any other value returns{" "}
                        <code>Transaction not found</code> (DPI-4002). Keep{" "}
                        <code>origRefNumber</code> unique on your side: if several transactions
                        share one, only one of them is returned.
                      </p>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Query Parameters"
                          headerTag="request"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameter</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                                <th>Min</th>
                                <th>Max</th>
                              </tr>
                            </thead>
                            <tbody>
                              
                              <tr>
                                <td>ttno</td>
                                <td>string</td>
                                <td>Transaction number, or your origRefNumber</td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p className="text-muted text-14 mb-0">
                          Omitting <code>ttno</code>, or sending it empty, returns 400 with
                          DPI-1000.
                        </p>
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Response"
                          headerTag="response"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameters</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>statusCode</td>
                                <td>integer</td>
                                <td>200 on success, otherwise the failure status</td>
                              </tr>
                              <tr>
                                <td>message</td>
                                <td>string</td>
                                <td>"Success", or the reason for the failure</td>
                              </tr>
                              <tr>
                                <td>errorCode</td>
                                <td>string</td>
                                <td>null on success; a stable DPI-xxxx code on failure</td>
                              </tr>
                              <tr>
                                <td>data.ttno</td>
                                <td>string</td>
                                <td>Transaction ID</td>
                              </tr>
                              <tr>
                                <td>data.sender.name</td>
                                <td>string</td>
                                <td>Sender name as recorded</td>
                              </tr>
                              <tr>
                                <td>data.receiver.name</td>
                                <td>string</td>
                                <td>Receiver name as recorded</td>
                              </tr>
                              <tr>
                                <td>data.receiver.phone</td>
                                <td>string</td>
                                <td>Receiver phone as recorded</td>
                              </tr>
                              <tr>
                                <td>data.bankDetails</td>
                                <td>null</td>
                                <td>Reserved; always null in the current version</td>
                              </tr>
                              <tr>
                                <td>data.amount</td>
                                <td>number</td>
                                <td>Sending amount</td>
                              </tr>
                              <tr>
                                <td>data.status</td>
                                <td>string</td>
                                <td>
                                  Current lifecycle status. See Transaction Status below for
                                  the known values and the recommended action for each.
                                </td>
                              </tr>
                              <tr>
                                <td>data.origRef</td>
                                <td>string</td>
                                <td>The origRefNumber you supplied on Send Remittance</td>
                              </tr>
                              <tr>
                                <td>data.date_Paid</td>
                                <td>string | null</td>
                                <td>Payout time, ISO 8601 without a timezone designator; null until paid</td>
                              </tr>
                              <tr>
                                <td>data.dated</td>
                                <td>string</td>
                                <td>Creation time in UTC, ISO 8601 without a timezone designator</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Transaction Status"
                          headerTag="status"
                        />
                        <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                          <code>data.status</code> reports where the transaction sits in
                          its lifecycle. Use it to decide whether a follow-up action is
                          still possible before you attempt one.
                        </p>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th className="text-nowrap">Status</th>
                                <th>Meaning</th>
                                <th>Recommended action</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-nowrap">Ready</td>
                                <td>
                                  Booked and available for the receiving agent to pay out.
                                  This is the initial state after a successful Send
                                  Remittance.
                                </td>
                                <td>
                                  No action needed. Cancel Remittance and Update
                                  Beneficiary Details succeed only in this status, and only
                                  until the paying agent downloads the transaction.
                                </td>
                              </tr>
                              <tr>
                                <td className="text-nowrap">HOLD</td>
                                <td>
                                  Payout is paused pending an operational check. The
                                  transaction is neither cancelled nor paid.
                                </td>
                                <td>
                                  Managed through the appropriate compliance and
                                  operational procedures.
                                </td>
                              </tr>
                              <tr>
                                <td className="text-nowrap">AML</td>
                                <td>
                                  Held for compliance screening. The transaction remains
                                  open while the review is carried out.
                                </td>
                                <td>
                                  Managed through the appropriate compliance and
                                  operational procedures.
                                </td>
                              </tr>
                              <tr>
                                <td className="text-nowrap">AML-HOLD</td>
                                <td>
                                  Held for compliance screening with an operational hold
                                  also applied. Payout cannot proceed until both are
                                  cleared.
                                </td>
                                <td>
                                  Managed through the appropriate compliance and
                                  operational procedures.
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        <div className="alert alert-warning text-14">
                          <strong>Follow-up actions require "Ready".</strong> Cancel
                          Remittance and Update Beneficiary Details succeed only for a
                          transaction that is yours, still <code>Ready</code>, and not yet
                          downloaded by the paying agent, and they accept the transaction
                          number only (not your <code>origRefNumber</code>). Any other case
                          &mdash; <code>HOLD</code>, <code>AML</code>,{" "}
                          <code>AML-HOLD</code>, already paid or cancelled, or an unknown
                          number &mdash; returns
                          <code> DPI-4001 &mdash; Transaction is not ready for
                          modification</code>; those two endpoints never return DPI-4002.
                          Query this endpoint first rather than relying on the cancel
                          attempt to tell you. Compare status values case-insensitively.
                        </div>

                        <div className="alert alert-secondary text-14">
                          <strong>Resolution time.</strong> As the resolution time will
                          depend on the nature of each case and the required compliance
                          review, we are not in a position to provide a definitive
                          timeline at this stage. Poll this endpoint for a change of
                          status rather than building against an expected window, and do
                          not treat a prolonged hold as a failed transaction. Do not
                          resubmit a held transaction &mdash; a retry creates a second
                          transaction.
                        </div>

                        <div className="alert alert-danger text-14">
                          <strong>Do not disclose compliance status to the customer.</strong>{" "}
                          When a transaction is in <code>AML</code> or
                          <code> AML-HOLD</code>, do not tell the sender or beneficiary
                          that it is under compliance or anti-money-laundering review.
                          Report it to them as "under review" or "processing" only, and
                          direct any queries to your Dahabshiil contact.
                        </div>

                        <div className="alert alert-info text-14 mb-0">
                          <strong>Treat the status list as open.</strong>{" "}
                          <code>status</code> reflects the operational state of the
                          transaction and further values may be introduced. Do not fail
                          hard on a value you do not recognise &mdash; treat it as
                          non-terminal, keep polling, and branch explicitly only on the
                          statuses you handle.
                        </div>
                      </div>


                      <div className="my-5">
                        <CodeBlock
                          code={responseCode}
                          codeBlackText="Response Example"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={failureResponseCode}
                          codeBlackText="Failure Example"
                          codeLanguage="json"
                        />
                        <p className="text-muted text-14 mt-3 mb-0">
                          Other failures: DPI-1000 when <code>ttno</code> is missing, and
                          DPI-6001 <code>Invalid input</code> when the authenticated user
                          could not be resolved to an agent account (see Get Balance).
                        </p>
                      </div>

                      <ErrorCodesLink />
                    </div>
                  </div>
                </div>
                <div className="col-md-5 pt-5">
                  <h5 className="card-title text-primary">
                     Code Samples
                  </h5>

                  <TryGetTransactionDetails
                    codeBlackTitle="Request Example"
                    transactionDetailsObject={requestCode}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GetTransactionDetails;
